// Property detail page (mobile + desktop) — thin orchestrator.
//
// Tracks design source:
//   /tmp/design-files/realestate/project/estate-property-detail-v2.jsx
//     - EstatePropertyDetailMobile  (lines 62–290)
//     - EstatePropertyDetailDesktop (lines 414–632)
//   /tmp/design-files/realestate/project/estate-quick-status.jsx (picker)
//
// Layout dispatch:
//   - `< 1024px` → `MobileLayout` (hero image with Back overlay +
//     bottom-sheet style content panel + sticky bottom action bar)
//   - `>= 1024px` → `DesktopLayout` (breadcrumb top bar + 1.5fr:1fr
//     gallery/details split + activity timeline pinned bottom-right)
//
// This file's job is route param → property fetch → loading/error
// branches → media-query dispatch + a thin <PropertyDetailLayout>
// shell that owns the AI / Schedule dialogs. All visual composition
// lives in `components/detail/*`.

import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ChevronLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/empty-state";
import { ApiError } from "@/lib/api-client";
import { useProperty, usePropertyImages } from "@/features/property/api";
import { PropertyDetailSkeleton } from "@/features/property/components/property-detail-skeleton";
import { GeneratePostDialog } from "@/features/ai/components/generate-post-dialog";
import { useMediaQuery } from "@/features/lead/hooks/use-media-query";
import { ScheduleViewingDialog } from "@/features/property/components/detail/schedule-viewing-dialog";
import { DesktopLayout } from "@/features/property/components/detail/desktop-layout";
import { MobileLayout } from "@/features/property/components/detail/mobile-layout";
import { placeholderGalleryImages } from "@/features/property/utils/placeholder-image";
import type { Property } from "@/types/property";

// ---------------------------------------------------------------------------
// Page entry
// ---------------------------------------------------------------------------

function parsePropertyId(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const n = Number.parseInt(raw, 10);
  if (Number.isNaN(n) || n <= 0) return undefined;
  return n;
}

export default function PropertyDetailPage() {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const propertyId = useMemo(() => parsePropertyId(params.id), [params.id]);

  const propertyQuery = useProperty(propertyId);

  if (propertyId === undefined) return <NotFoundState />;
  if (propertyQuery.isPending) return <PropertyDetailSkeleton />;
  if (propertyQuery.isError) {
    const error = propertyQuery.error;
    if (error instanceof ApiError && error.status === 404) return <NotFoundState />;
    return <ErrorState onRetry={() => void propertyQuery.refetch()} />;
  }

  return (
    <PropertyDetailLayout
      property={propertyQuery.data}
      onBack={() => navigate("/properties")}
    />
  );
}

// ---------------------------------------------------------------------------
// Shared layout dispatcher
// ---------------------------------------------------------------------------

interface LayoutProps {
  property: Property;
  onBack: () => void;
}

function PropertyDetailLayout({ property, onBack }: LayoutProps) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [aiOpen, setAiOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const imagesQuery = usePropertyImages(property.id);
  const realImages = imagesQuery.data ?? [];
  // An image-less property gets a 3-photo placeholder gallery so the detail
  // carousel is never empty. Never stored — a real upload replaces the set.
  // Gated on isSuccess so a property that DOES have photos doesn't flash
  // placeholders while its images load.
  const images =
    imagesQuery.isSuccess && realImages.length === 0
      ? placeholderGalleryImages(property.id)
      : realImages;

  return (
    <>
      {isDesktop ? (
        <DesktopLayout
          property={property}
          images={images}
          onBack={onBack}
          onOpenAi={() => setAiOpen(true)}
          onOpenSchedule={() => setScheduleOpen(true)}
        />
      ) : (
        <MobileLayout
          property={property}
          images={images}
          onBack={onBack}
          onOpenSchedule={() => setScheduleOpen(true)}
        />
      )}

      <GeneratePostDialog
        property={property}
        open={aiOpen}
        onOpenChange={setAiOpen}
      />
      <ScheduleViewingDialog
        property={property}
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        isDesktop={isDesktop}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Empty / error states
// ---------------------------------------------------------------------------

function NotFoundState() {
  return (
    <EmptyState
      title="Không tìm thấy bất động sản"
      description="Bất động sản này không còn tồn tại"
      action={
        <Button asChild variant="outline">
          <Link to="/properties">
            <ChevronLeft aria-hidden="true" />
            Quay lại danh sách
          </Link>
        </Button>
      }
    />
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <EmptyState
      title="Không thể tải bất động sản"
      description="Vui lòng kiểm tra kết nối mạng và thử lại."
      action={
        <Button variant="outline" onClick={onRetry}>
          Thử lại
        </Button>
      }
    />
  );
}

